import { useState } from 'react';
import { Button } from '@/components/ui/button.jsx';
import { Input } from '@/components/ui/input.jsx';
import { toast } from '@/components/ui/toaster.jsx';
import { api } from '@/lib/api.js';
import { announceLedgerChanged } from '@/services/deals.service.js';
import { useCarrierDocPick, ASKED } from './CarrierDocPick.jsx';
import { ledgerRowName } from './CarrierDocAttach.jsx';

// Inline confirm for a signed WHOLE-POLICY cancellation. Same shape as the
// endorsement confirm in TransactionHistoryPanel and deliberately a separate control: this one records
// the carrier's return premium and nothing else, because a whole-policy
// cancellation has no roster to move. Until this existed, a cancellation's
// premium_delta could never be written at all.
// Rendered on the ledger row and by the cancellation note (label: its own words for the same door). The carrier's document may
// come with the return premium (paperwork-1 1b): filed through the deal's document door and handed to the door as
// carrier_document_id (CarrierDocPick).
export default function ConfirmCancellationInline({ dealId, txn, onConfirmed, label = 'Record carrier return premium' }) {
  const [formOpen, setFormOpen] = useState(false);
  const [returnPremium, setReturnPremium] = useState('');
  const [busy, setBusy] = useState(false);
  const carrierDoc = useCarrierDocPick({ dealId, txnId: txn.id, rowName: ledgerRowName('cancellation', txn.effective_date) });

  async function confirm() {
    const rp = Number(returnPremium);
    if (returnPremium === '' || !Number.isFinite(rp) || rp < 0) {
      toast.error('Return premium must be a non-negative number (0 is allowed)');
      return;
    }
    setBusy(true);
    try {
      const carrierDocumentId = await carrierDoc.documentId();
      if (carrierDocumentId === ASKED) return;
      const res = await api.post(
        `/api/lpr/deal/${encodeURIComponent(dealId)}/transaction/${encodeURIComponent(txn.id)}/confirm-cancellation`,
        { return_premium: rp, ...(carrierDocumentId ? { carrier_document_id: carrierDocumentId } : {}) }
      );
      if (res?.success === false) throw new Error(res?.error || 'Confirm failed');
      if (carrierDocumentId && res?.carrier_document?.id !== carrierDocumentId) throw new Error('The return premium was recorded, but the carrier\'s document did not read back linked to this cancellation');
      toast.success(res?.carrier_document?.name ? `Carrier return premium recorded, with ${res.carrier_document.name}` : 'Carrier return premium recorded');
      for (const w of res?.warnings || []) toast.info(w);
      announceLedgerChanged(dealId);
      onConfirmed?.();
    } catch (err) {
      toast.error(err?.body?.error || err.message || 'Confirm failed');
    } finally {
      setBusy(false);
    }
  }

  if (!formOpen) {
    return (
      <div className="mt-1.5">
        <Button size="sm" variant="outline" onClick={() => setFormOpen(true)}>
          {label}
        </Button>
      </div>
    );
  }
  return (
    <div className="mt-1.5 flex flex-wrap items-center gap-2">
      <Input
        type="number"
        min="0"
        step="0.01"
        value={returnPremium}
        onChange={(e) => setReturnPremium(e.target.value)}
        placeholder="Return premium $"
        className="h-8 w-40 text-xs"
      />
      <Button size="sm" onClick={confirm} disabled={busy}>
        {busy ? 'Recording…' : 'Record return'}
      </Button>
      <Button size="sm" variant="ghost" onClick={() => setFormOpen(false)} disabled={busy}>
        Cancel
      </Button>
      {carrierDoc.field}
    </div>
  );
}
